import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SpaceBackground from '../components/SpaceBackground';
import {
  Menu,
  X,
  Calendar,
  MapPin,
  Target,
  Rocket,
  Quote
} from 'lucide-react';

const WhoWeAre = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const founders = [
    { name: 'Abhijit Pandit', role: 'Co-Founder & CEO', image: 'https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/ud7c9dk5_abhijit.PNG' },
    { name: 'Jaydeep Kotak', role: 'Co-Founder & CFO', image: 'https://customer-assets.emergentagent.com/job_responsive-website-1/artifacts/qlrpgaiy_jaydeep.PNG' },
    { name: 'Rudolph Dessa', role: 'Co-Founder & VP, Business Development', image: 'https://customer-assets.emergentagent.com/job_responsive-website-1/artifacts/aggx4xah_rudolph.PNG' },
    { name: 'Soham Mehta', role: 'Co-Founder', image: 'https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/cx60vmps_soham.PNG' }
  ];

  const timelineData = [
    {
      year: '2022',
      label: 'THE BEGINNING',
      title: 'Where It All Began',
      description: 'Moonrock was established following the relocation of our founders\' crypto-mining operations from India to Oman, driven by the Sultanate\'s progressive technology ecosystem and forward-looking national priorities.',
      icon: Calendar,
      color: '#7C5CFF'
    },
    {
      year: '',
      label: 'FIRST DELIVERY',
      title: 'Green Data City Pilot',
      description: 'Our journey began with hands-on delivery through a pilot project at Green Data City, developed in collaboration with the Ministry of Transport, Communications and Information Technology (MTCIT). This initial deployment set the foundation for how we operate: solving practical problems in real environments, where execution matters more than theory.',
      icon: MapPin,
      color: '#34D399'
    },
    {
      year: '',
      label: 'EVOLUTION',
      title: 'A Repeatable Execution Model',
      description: 'Building on early delivery, MRD refined a repeatable execution model—moving systematically from opportunity identification to pilot deployment and full-scale implementation. This approach is supported by embedded teams, disciplined governance, and strong local partnerships.',
      icon: Target,
      color: '#FBBF24'
    },
    {
      year: 'TODAY',
      label: 'PRESENT',
      title: 'Building Ventures That Scale',
      description: 'Today, Moonrock serves as a launchpad for ventures operating at the intersection of technology readiness, market demand, and regulatory alignment. We co-build businesses that scale efficiently, remain resilient over time, and contribute meaningfully to Oman\'s ambition to become a regional hub for technology-led growth.',
      icon: Rocket,
      color: '#F472B6',
      highlight: true
    }
  ];

  return (
    <div className="who-we-are-page" data-testid="who-we-are-page" style={{ overflowX: 'hidden' }}>
      <SpaceBackground />

      <div className="page-content" style={{ position: 'relative', zIndex: 1 }}>
        {/* Navbar */}
        <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="navbar">
          <div className="header-content">
            <div className="logo-container">
              <Link to="/">
                <img 
                  src="/moonrock-logo-light.png" 
                  alt="MoonRock Research & Development" 
                  className="navbar-logo-image"
                />
              </Link>
            </div>
            
            <nav className="nav-menu" data-testid="nav-menu">
              <Link to="/who-we-are" className="nav-link-page active">Who We Are</Link>
              <Link to="/#how-we-work" className="nav-link-page">How We Work</Link>
              <Link to="/#industries" className="nav-link-page">Focus</Link>
              <Link to="/projects" className="nav-link-page">Projects</Link>
            </nav>

            <div className="nav-cta-wrapper">
              <Link to="/contact">
                <Button className="nav-cta" data-testid="nav-cta">Partner With Us</Button>
              </Link>
            </div>

            <button 
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <nav className="mobile-nav-links">
              <Link to="/who-we-are" className="active" onClick={() => setIsMobileMenuOpen(false)}>Who We Are</Link>
              <Link to="/#how-we-work" onClick={() => setIsMobileMenuOpen(false)}>How We Work</Link>
              <Link to="/#industries" onClick={() => setIsMobileMenuOpen(false)}>Focus</Link>
              <Link to="/projects" onClick={() => setIsMobileMenuOpen(false)}>Projects</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="mobile-nav-cta">Partner With Us</Button>
              </Link>
            </nav>
          </div>
        </header>

        {/* Hero - Our Journey Header */}
        <section style={{
          padding: '160px 0 60px',
          width: '100%',
          maxWidth: '100%'
        }} data-testid="wwa-hero">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 2rem',
            textAlign: 'center'
          }}>
            <span style={{
              display: 'inline-block',
              fontSize: '0.8rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              color: '#CBB8FF',
              marginBottom: '20px',
              padding: '10px 24px',
              background: 'rgba(203,184,255,0.1)',
              border: '1px solid rgba(203,184,255,0.2)',
              borderRadius: '100px'
            }}>Our Journey</span>
            <h1 style={{
              fontSize: 'clamp(2.25rem, 5vw, 3.25rem)',
              fontWeight: 800,
              color: '#FFFFFF',
              margin: '0 0 20px 0',
              letterSpacing: '-0.03em',
              lineHeight: 1.15
            }}>
              From First Build to Scale-Ready Ventures
            </h1>
            <p style={{
              fontSize: '1.15rem',
              color: '#A8A0B8',
              lineHeight: 1.7,
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              A journey shaped in Oman—designed for regional impact.
            </p>
          </div>
        </section>

        {/* Timeline Section - Vertical Flow */}
        <section style={{
          padding: '60px 0 100px',
          width: '100%'
        }} data-testid="wwa-journey">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 2rem',
            position: 'relative'
          }}>
            {/* Central Timeline Line */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '0',
              bottom: '0',
              width: '3px',
              background: 'linear-gradient(180deg, #7C5CFF 0%, #34D399 33%, #FBBF24 66%, #F472B6 100%)',
              transform: 'translateX(-50%)',
              borderRadius: '3px'
            }} className="wwa-timeline-line"></div>

            {/* Timeline Items */}
            {timelineData.map((item, index) => {
              const IconComponent = item.icon;
              const isLeft = index % 2 === 0;
              
              return (
                <div key={index} className="wwa-timeline-item" style={{
                  display: 'flex',
                  justifyContent: isLeft ? 'flex-start' : 'flex-end',
                  position: 'relative',
                  marginBottom: index < timelineData.length - 1 ? '60px' : '0'
                }}>
                  {/* Timeline Node */}
                  <div className="wwa-timeline-node" style={{
                    position: 'absolute',
                    left: '50%',
                    top: '30px',
                    transform: 'translateX(-50%)',
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: item.highlight 
                      ? `linear-gradient(135deg, ${item.color} 0%, ${item.color}88 100%)`
                      : 'rgba(20, 15, 40, 0.9)',
                    border: `3px solid ${item.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    boxShadow: item.highlight 
                      ? `0 0 30px ${item.color}60`
                      : `0 0 20px ${item.color}30`
                  }}>
                    <IconComponent size={24} color={item.highlight ? '#fff' : item.color} />
                  </div>

                  {/* Content Card */}
                  <div className="wwa-timeline-card" style={{
                    width: 'calc(50% - 60px)',
                    background: item.highlight 
                      ? 'linear-gradient(135deg, rgba(244, 114, 182, 0.12) 0%, rgba(20, 15, 40, 0.8) 100%)'
                      : 'rgba(20, 15, 40, 0.7)',
                    backdropFilter: 'blur(16px)',
                    border: item.highlight 
                      ? '1px solid rgba(244, 114, 182, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '20px',
                    padding: '32px 36px',
                    position: 'relative'
                  }}>
                    {/* Arrow pointing to timeline - hidden on mobile */}
                    <div className="wwa-timeline-arrow" style={{
                      position: 'absolute',
                      top: '32px',
                      [isLeft ? 'right' : 'left']: '-12px',
                      width: '24px',
                      height: '24px',
                      background: item.highlight 
                        ? 'linear-gradient(135deg, rgba(244, 114, 182, 0.12) 0%, rgba(20, 15, 40, 0.8) 100%)'
                        : 'rgba(20, 15, 40, 0.7)',
                      border: item.highlight 
                        ? '1px solid rgba(244, 114, 182, 0.3)'
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      borderRight: isLeft ? 'none' : undefined,
                      borderBottom: isLeft ? 'none' : undefined,
                      borderLeft: !isLeft ? 'none' : undefined,
                      borderTop: !isLeft ? 'none' : undefined,
                      transform: 'rotate(45deg)'
                    }}></div>

                    {/* Year/Label */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      {item.year && (
                        <span style={{
                          fontSize: '0.9rem',
                          fontWeight: 800,
                          color: item.color,
                          letterSpacing: '0.05em'
                        }}>{item.year}</span>
                      )}
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: item.highlight ? '#F9A8D4' : '#8B7FA8',
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase'
                      }}>{item.label}</span>
                    </div>

                    {/* Title */}
                    <h3 style={{
                      fontSize: '1.4rem',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      margin: '0 0 16px 0',
                      lineHeight: 1.3
                    }}>{item.title}</h3>

                    {/* Description */}
                    <p style={{
                      fontSize: '0.95rem',
                      lineHeight: 1.8,
                      color: item.highlight ? '#D4C0E0' : '#A8A0B8',
                      margin: 0,
                      textAlign: 'left'
                    }}>{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Leadership Section */}
        <section style={{
          padding: '80px 0 100px',
          width: '100%'
        }} data-testid="wwa-leadership">
          <div style={{
            maxWidth: '1100px',
            margin: '0 auto',
            padding: '0 2rem'
          }}>
            {/* Section Header */}
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <span style={{
                display: 'inline-block',
                fontSize: '0.8rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                color: '#CBB8FF',
                marginBottom: '16px',
                padding: '10px 24px',
                background: 'rgba(203,184,255,0.1)',
                border: '1px solid rgba(203,184,255,0.2)',
                borderRadius: '100px'
              }}>Leadership</span>
              <h2 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                color: '#FFFFFF',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>Our Chairman</h2>
            </div>

            {/* Chairman Card - Premium Layout */}
            <div className="wwa-chairman-card" data-testid="wwa-chairman-card">
              {/* Photo Container - Aligned with content */}
              <div className="wwa-chairman-photo" style={{
                display: 'flex',
                alignItems: 'stretch'
              }}>
                <div style={{
                  width: '100%',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  border: '4px solid rgba(124, 92, 255, 0.3)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_starfield-premium/artifacts/4h0xja10_AHMED_UNCLE.jpeg"
                    alt="Ahmed Salim Al-Wahaibi"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top'
                    }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="wwa-chairman-content" style={{ paddingLeft: '48px' }}>
                {/* Name & Title */}
                <h3 style={{
                  fontSize: '2rem',
                  fontWeight: 800,
                  color: '#FFFFFF',
                  margin: '0 0 8px 0',
                  letterSpacing: '-0.02em'
                }}>Ahmed Salim Al-Wahaibi</h3>
                <p style={{
                  fontSize: '1.1rem',
                  color: '#CBB8FF',
                  margin: '0 0 32px 0',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}>Chairman</p>

                {/* Quote */}
                <div style={{
                  position: 'relative',
                  padding: '24px 28px',
                  background: 'rgba(124, 92, 255, 0.1)',
                  border: '1px solid rgba(124, 92, 255, 0.2)',
                  borderRadius: '16px',
                  marginBottom: '32px'
                }}>
                  <Quote size={24} style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '24px',
                    color: '#7C5CFF',
                    background: 'rgba(20, 15, 40, 1)',
                    padding: '4px'
                  }} />
                  <p style={{
                    fontSize: '1.35rem',
                    fontStyle: 'italic',
                    fontWeight: 500,
                    color: '#E8E0F0',
                    lineHeight: 1.5,
                    margin: 0
                  }}>
                    "Predict the ball, don't follow it !"
                  </p>
                </div>

                {/* Bio - Exact text */}
                <p style={{
                  fontSize: '1rem',
                  lineHeight: 1.85,
                  color: '#B8B0C8',
                  margin: '0 0 20px 0',
                  textAlign: 'justify'
                }}>
                  Ahmed Salim Al-Wahaibi is a visionary business leader with a distinguished track record in launching and scaling multi-billion-dollar industrial projects. As former Group CEO of Oman Oil Company, he oversaw 33 subsidiaries spanning upstream to downstream operations.
                </p>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: 1.85,
                  color: '#B8B0C8',
                  margin: 0,
                  textAlign: 'justify'
                }}>
                  He has held leadership roles across Omani and international boards, driving investment diversification, corporate restructuring, and long-term socio-economic impact through joint ventures and CSR initiatives. Ahmed holds a Master's degree in Development Management from the American University, Washington DC, a Bachelor's degree in Electrical Technology from Washington University, and executive certifications from IMD Switzerland.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Founders Section - Redesigned */}
        <section style={{
          padding: '80px 0 120px',
          width: '100%'
        }} data-testid="wwa-founders">
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <span style={{
                display: 'inline-block',
                fontSize: '0.8rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                color: '#CBB8FF',
                marginBottom: '16px',
                padding: '10px 24px',
                background: 'rgba(203,184,255,0.1)',
                border: '1px solid rgba(203,184,255,0.2)',
                borderRadius: '100px'
              }}>The Team</span>
              <h2 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                color: '#FFFFFF',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>Meet Our Founders</h2>
            </div>

            <div className="wwa-founders-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '28px'
            }}>
              {founders.map((founder, index) => (
                <div key={index} data-testid={`founder-${index}`} style={{
                  background: 'linear-gradient(180deg, rgba(30, 25, 55, 0.8) 0%, rgba(20, 15, 40, 0.9) 100%)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '24px',
                  padding: '0',
                  overflow: 'hidden',
                  transition: 'all 0.4s ease',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                }}>
                  {/* Image Container */}
                  <div style={{
                    width: '100%',
                    height: '280px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <img 
                      src={founder.image} 
                      alt={founder.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top'
                      }}
                    />
                    {/* Gradient Overlay */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '80px',
                      background: 'linear-gradient(180deg, transparent 0%, rgba(20, 15, 40, 0.95) 100%)'
                    }}></div>
                  </div>

                  {/* Content */}
                  <div style={{
                    padding: '24px 28px 32px',
                    textAlign: 'center'
                  }}>
                    <h3 style={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      margin: '0 0 10px 0'
                    }}>{founder.name}</h3>
                    <p style={{
                      fontSize: '0.9rem',
                      color: '#CBB8FF',
                      margin: 0,
                      lineHeight: 1.5,
                      fontWeight: 500
                    }}>{founder.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer" data-testid="footer">
          <div className="footer-glass">
            <div className="footer-content">
              <div className="footer-grid">
                <div className="footer-brand">
                  <div className="footer-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/8gbuyg09_Full%20Logo%20Light%20TP%20space%20%281%29.png" 
                      alt="MoonRock R&D" 
                      style={{ height: '56px', width: 'auto', marginBottom: '14px', objectFit: 'contain' }}
                    />
                    <p className="footer-tagline" style={{ margin: 0, paddingLeft: '10px' }}>We Shape What Lasts.</p>
                  </div>
                </div>

                <div className="footer-links">
                  <h4 className="footer-heading">Quick Links</h4>
                  <ul>
                    <li><Link to="/">Home</Link></li>
                    <li><a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Who We Are</a></li>
                    <li><Link to="/#how-we-work">How We Work</Link></li>
                    <li><Link to="/#industries">Focus</Link></li>
                    <li><Link to="/projects">Projects</Link></li>
                  </ul>
                </div>

                <div className="footer-contact">
                  <h4 className="footer-heading">Contact</h4>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span>Suite 403, Bldg. 393, 18th November St, Muscat</span>
                    </div>
                    <div className="contact-item">
                      <a href="mailto:admin@moonrock.om">admin@moonrock.om</a>
                    </div>
                    <div className="contact-item">
                      <a href="tel:+96896607256">+968 9660 7256</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer-divider"></div>
              <div className="footer-bottom">
                <p className="footer-copyright">© 2026 MoonRock R&D. All rights reserved.</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default WhoWeAre;
